import { Link, useLocation, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationBell } from '@/components/NotificationBell';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Package,
  Truck,
  Building2,
  FileText,
  Activity,
  LogOut,
  Zap,
  Menu,
  X,
  BarChart3,
  Bell,
  AlertTriangle,
  Settings,
  Sun,
  Moon,
  ChevronDown,
  Radio,
  Rss,
  CreditCard,
  TrendingUp,
  Star,
  Search,
  Shield,
  Ban,
  DollarSign,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function KpiAlertBanner({ token }) {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!token) return;
    const fetchKpi = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/kpi`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAlerts(data.alerts || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchKpi();
    const interval = setInterval(fetchKpi, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  if (alerts.length === 0) return null;

  return (
    <div className="mb-4 space-y-2" data-testid="kpi-alert-banner">
      {alerts.map((alert, i) => (
        <div
          key={i}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${
            alert.severity === 'critical'
              ? 'bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400'
              : 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400'
          }`}
          data-testid={`kpi-alert-${alert.type}`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span>{alert.message}</span>
        </div>
      ))}
    </div>
  );
}

function NavDropdown({ item, isAnyChildActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const active = isAnyChildActive(item.children);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`
          relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium
          transition-colors whitespace-nowrap
          ${active
            ? 'bg-accent/10 text-accent'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }
        `}
      >
        <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        <span>{item.label}</span>
        <ChevronDown className={`h-3 w-3 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-48 rounded-xl bg-card border border-border shadow-lg py-1 z-50">
          {item.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              className={`
                flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium transition-colors
                ${location.pathname === child.href
                  ? 'text-accent bg-accent/5'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <child.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export const AdminLayout = () => {
  const { user, token, logout, loading } = useAuth();
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const navItems = [
    { href: '/admin',               icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/loads',         icon: Package,         label: 'Loads'     },
    {
      label: 'Carriers',
      icon: Truck,
      children: [
        { href: '/admin/carriers',        icon: Truck,   label: 'All Carriers' },
        { href: '/admin/carriers/search', icon: Search,  label: 'Search'       },
      ],
    },
    { href: '/admin/shippers',      icon: Building2,       label: 'Shippers'  },
    { href: '/admin/kpi',              icon: BarChart3,   label: 'KPI'           },
    {
      label: 'Central Dispatch',
      icon: DollarSign,
      children: [
        { href: '/admin/cd-price-check', icon: DollarSign, label: 'CD Pricing'  },
        { href: '/admin/cd-listings',    icon: Package,    label: 'CD Listings' },
      ],
    },
    { href: '/admin/notifications',    icon: Bell,        label: 'Alerts', badge: unreadCount },
    {
      label: 'Fraud',
      icon: Shield,
      children: [
        { href: '/admin/fraud',           icon: Shield, label: 'Dashboard'  },
        { href: '/admin/fraud/cases',     icon: AlertTriangle, label: 'Cases' },
        { href: '/admin/fraud/blocklist', icon: Ban,    label: 'IP Controls' },
      ],
    },
    {
      label: 'Reports',
      icon: TrendingUp,
      children: [
        { href: '/admin/reports/payments',          icon: CreditCard,  label: 'Payments'         },
        { href: '/admin/reports/load-performance',  icon: BarChart3,   label: 'Load Performance' },
        { href: '/admin/reports/carrier-scorecard', icon: Star,        label: 'Carrier Scorecard'},
      ],
    },
    {
      label: 'Settings',
      icon: Settings,
      children: [
        { href: '/admin/settings',        icon: Settings,  label: 'ENV & APIs'      },
        { href: '/admin/subscriptions',   icon: Rss,       label: 'Subscriptions' },
        { href: '/admin/events',          icon: Radio,     label: 'Events'        },
        { href: '/admin/connector-health',icon: Activity,  label: 'Connectors'    },
        { href: '/admin/audit-logs',      icon: FileText,  label: 'Audit'         },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;
  const isAnyChildActive = (children) => children.some((c) => location.pathname === c.href);

  const allFlatItems = navItems.flatMap((item) =>
    item.children ? item.children : [item]
  );

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    setUserMenuOpen(false);
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AD';

  return (
    <div className="min-h-screen bg-background">

      {/* ── Top Navigation Bar ── */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border shadow-sm">
        <div className="flex items-center h-full px-4 gap-3">

          {/* Logo */}
          <Link
            to="/admin"
            className="flex items-center gap-2 shrink-0 mr-1"
            data-testid="admin-nav-logo"
          >
            <Zap className="h-5 w-5 text-accent" strokeWidth={1.5} />
            <span className="font-outfit font-semibold text-base tracking-tight text-foreground">
              ShipTesla
            </span>
          </Link>

          {/* Divider */}
          <div className="h-6 w-px bg-border shrink-0" />

          {/* Nav Items — Desktop */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1">
            {navItems.map((item) =>
              item.children ? (
                <NavDropdown key={item.label} item={item} isAnyChildActive={isAnyChildActive} />
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`
                    relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium
                    transition-colors whitespace-nowrap
                    ${isActive(item.href)
                      ? 'bg-accent/10 text-accent'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {item.badge > 0 && (
                    <span className="h-4 min-w-[16px] flex items-center justify-center bg-accent text-accent-foreground text-[10px] font-bold rounded-full px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              )
            )}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1 ml-auto shrink-0">

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark'
                ? <Sun className="h-4 w-4" />
                : <Moon className="h-4 w-4" />
              }
            </button>

            {/* Notification Bell */}
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              markAsRead={markAsRead}
            />

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium
                  text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <div className="h-7 w-7 rounded-full bg-accent/15 text-accent flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials}
                </div>
                <span className="hidden lg:block max-w-[100px] truncate text-[13px]">
                  {user?.name}
                </span>
                <ChevronDown className="h-3.5 w-3.5 hidden lg:block opacity-60" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-card border border-border shadow-lg py-1 z-50">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm
                      text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid="admin-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {mobileOpen && (
          <div className="md:hidden bg-card border-b border-border shadow-lg">
            <nav className="p-2 grid grid-cols-2 gap-1">
              {allFlatItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`
                    flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${isActive(item.href)
                      ? 'bg-accent/10 text-accent'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                >
                  <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="h-4 min-w-[16px] flex items-center justify-center bg-accent text-accent-foreground text-[10px] font-bold rounded-full px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* ── Main Content ── */}
      <main className="pt-14 min-h-screen">
        <div className="px-6 py-6 max-w-screen-2xl mx-auto">
          <KpiAlertBanner token={token} />
          <Outlet />
        </div>
      </main>
    </div>
  );
};
